import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import './Toast.css';

/**
 * App-wide transient notifications (replaces window.alert). Hand-rolled — no
 * dependency, matching the Button design-system convention.
 *
 * Wrapped once at the root (src/index.js). Consume with the hook:
 *
 *   const toast = useToast();
 *   toast.success('Saved.');
 *   toast.error('Could not save.');
 *   toast.info('Working…');
 *
 * Each returns an id; `toast.dismiss(id)` removes it early. Errors default to a
 * longer timeout; pass `{ duration: 0 }` to make a toast sticky.
 */
const ToastContext = createContext(null);

let _idSeq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
    const timer = timers.current[id];
    if (timer) {
      clearTimeout(timer);
      delete timers.current[id];
    }
  }, []);

  const show = useCallback(
    (message, { type = 'info', duration = 5000 } = {}) => {
      const id = ++_idSeq;
      setToasts((ts) => [...ts, { id, message, type }]);
      if (duration > 0) {
        timers.current[id] = setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      show,
      success: (m, o) => show(m, { ...o, type: 'success' }),
      error: (m, o) => show(m, { duration: 8000, ...o, type: 'error' }),
      info: (m, o) => show(m, { ...o, type: 'info' }),
      dismiss,
    }),
    [show, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container" role="region" aria-label="Notifications" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.type}`}>
            <span className="toast__msg">{t.message}</span>
            <button
              type="button"
              className="toast__close"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
